import { legalEntity } from '../../legal';
import type { Translation } from './index';

export const en: Translation = {
  meta: {
    title: 'Denqfabrik — Integrations between shop, ERP and marketplace',
    description:
      'Denqfabrik builds, tests and operates integrations for retail and e-commerce teams: orders, inventory and prices move automatically between shop, ERP and marketplace.',
    ogTitle:
      'Denqfabrik — Integrations between shop, ERP and marketplace. Built, tested, operated.',
    ogDescription:
      'Orders, inventory and prices move automatically between your systems — WooCommerce, Shopware, JTL, Amazon, Mirakl and more. No manual re-entry.',
  },
  nav: {
    home: 'Home',
    agents: 'AI Agents',
    useCases: 'Use Cases',
    solutions: 'Solutions',
    integrations: 'Integrations',
    configurator: 'Configurator',
    pricing: 'Pricing',
    amazonSpApi: 'Amazon SP-API',
    about: 'About',
    contact: 'Contact',
    services: 'Use Cases',
    safety: 'Safety',
    safetyLabel: 'Building safe AI agents',
    safeAgents: 'Safe AI Agents',
    process: 'Process',
    blog: 'Blog',
    cta: 'Start a conversation',
    languageSwitch: 'DE',
    resources: 'Resources',
    resourcesSafetyDesc: 'Guide to robust AI systems',
    resourcesProjectsDesc: 'Real projects with real results',
    resourcesIntegrationsDesc: 'Connect ERP, marketplaces & tools',
    resourcesBlogDesc: 'View all posts',
    resourcesAboutDesc: 'Team & mission',
    resourcesLatestPosts: 'Latest posts',
  },
  hero: {
    badge: 'For retail and e-commerce teams in the DACH region',
    headlineLine1: 'Integrations between shop, ERP and marketplace.',
    headlineLine2: 'Built, tested, operated.',
    subheadline:
      'Orders, inventory and prices move automatically between your systems — WooCommerce, Shopware, JTL, Amazon, Mirakl and more. No manual re-entry, no toolkit.',
    ctaPrimary: 'Configure your integration',
    ctaSecondary: 'Request a project',
    flow: {
      sourcesLabel: 'Your systems',
      targetsLabel: 'Channels & targets',
      hubTitle: 'Denqfabrik Sync',
      hubItems: ['Orders', 'Inventory', 'Prices'],
      hubNote: 'Tested up front, continuously monitored, robust under failure.',
    },
  },
  trust: {
    subtitle: 'Commerce companies choose to',
    headingPrefix: 'Build with',
    ctaButton: 'Hear from our customers',
    title: 'Trusted by commerce companies across Europe',
    description:
      'Denqfabrik ships production systems in real retail workflows, not demo-only showcases.',
    logoWallTitle: 'Selected companies',
    testimonials: [
      {
        quote:
          'Because our business is highly seasonal, we need stable processes. René automated critical workflows so we can sell smoothly even during peak season.',
        author: 'Selma Kirsch',
        role: 'CEO',
        company: 'Alpenmärchen GmbH',
      },
      {
        quote:
          'We run a dropshipping model. Denqfabrik built partner warehouse interfaces in Plentymarkets and fully automated our order handling.',
        author: 'Fiona Grubits',
        role: 'CEO',
        company: 'Playflip',
      },
      {
        quote:
          'Interfaces change constantly, and marketplaces often do not notify us. We need someone reliable when critical flows break, and that is exactly what we get with René.',
        author: 'Waldemar Derksen',
        role: 'CEO',
        company: 'Albatros International GmbH',
      },
    ],
  },
  services: {
    label: 'USE CASES',
    title: 'Typical agents we build',
    description: 'Concrete deployment patterns instead of generic "AI for everything" claims.',
    cards: [
      {
        title: 'Support & case handling agent',
        description: 'Automate standard support cases and escalate edge cases safely.',
        points: [
          'Email classification and routing',
          'Standard cancellations, returns, and refunds',
          'Context access across ERP, shop, and logistics',
          'Human-in-the-loop escalation on uncertainty',
        ],
      },
      {
        title: 'Marketplace operations agent',
        description: 'Monitor marketplace operations and surface risky anomalies early.',
        points: [
          'Stock and pricing monitoring with suggestions',
          'Vendor process handling and edge cases',
          'Failed flow detection and triage',
          'Targeted escalation for high-risk events',
        ],
      },
      {
        title: 'Content & product data agent',
        description: 'Generate structured product content and validate before publish.',
        points: [
          'Structured HTML product descriptions',
          'Image- and context-based generation',
          'Validation for style, format, and compliance',
          'Review-ready drafts for teams',
        ],
      },
      {
        title: 'Finance & reconciliation agent',
        description: 'Automate checks, detect differences, and prepare finance reporting.',
        points: [
          'Payment reconciliation and fee checks',
          'Flagging mismatches and anomalies',
          'Reporting for finance operations',
          'Accounting-ready preparation output',
        ],
      },
    ],
  },
  safety: {
    title: 'Enterprise AI agents you can trust.',
    description: 'An AI agent without a safety architecture is not an asset, it is a risk.',
    statement:
      'We design failure modes, guardrails, and escalation paths before the first production action.',
    principles: [
      {
        title: "Agents must know what they don't know",
        description:
          'Out-of-distribution detection and abstention strategies stop actions under uncertainty instead of hallucinating.',
      },
      {
        title: 'Every decision needs an audit trail',
        description: 'Inputs, context, decision, guardrails, and outcomes are logged end-to-end.',
      },
      {
        title: 'Guardrails are architectural, not cosmetic',
        description:
          'Policies, limits, approvals, and fallbacks are part of the core system design.',
      },
      {
        title: 'Failure modes are designed, not discovered',
        description:
          'Circuit breakers, graceful degradation, and human escalation are defined upfront.',
      },
    ],
    metrics: [],
  },
  process: {
    title: 'From problem to production-safe agent.',
    description:
      'Four-step delivery model: clear scope, safety-first design, and stable operations after go-live.',
    steps: [
      {
        title: 'Discovery',
        description:
          'Map commerce workflows, identify automation potential, and define safety boundaries before implementation.',
      },
      {
        title: 'Architecture',
        description:
          'Design agent logic with guardrails, decision limits, and escalation routes at the core.',
      },
      {
        title: 'Build & verify',
        description:
          'Iterative development with verification against test suites and edge-case scenarios.',
      },
      {
        title: 'Deploy & monitor',
        description:
          'Production rollout with monitoring, anomaly detection, and circuit breakers in place.',
      },
    ],
  },
  integration: {
    title: 'Your systems. Securely connected.',
    highlight: 'MCP engineering from production',
    description:
      'We don\u2019t build demos \u2014 we build MCP servers, API pipelines, and real-time connectors that run in your production environment.',
    systems: [
      'Shopify / WooCommerce / Shopware',
      'Plentymarkets',
      'Marketplaces (OTTO, Mirakl, Amazon, and others)',
      'ERP systems (for example SAP, WeClapp, custom ERPs)',
      'Fulfillment and logistics systems',
    ],
    note: 'Detailed tooling and connector lists belong on the solutions pages. Amazon remains a dedicated special case.',
    platforms: [
      'SAP',
      'plentymarkets',
      'Xentral',
      'Odoo',
      'Dynamics 365',
      'Sage',
      'Shopify',
      'Shopware',
      'WooCommerce',
      'Magento',
      'BigCommerce',
      'PrestaShop',
      'Amazon',
      'OTTO',
      'Kaufland',
      'eBay',
      'Zalando',
      'Allegro',
      'Etsy',
      'Temu',
      'ManoMano',
      'Walmart',
      'Galaxus',
      'Klaviyo',
      'HubSpot',
      'Salesforce',
      'Zendesk',
      'Brevo',
      'DHL',
      'FedEx',
      'UPS',
    ],
    featured: ['Amazon', 'Shopify', 'plentymarkets'],
    featuredCards: [
      {
        title: 'Amazon & marketplaces',
        description:
          'SP-API, OTTO, eBay, Kaufland, Mirakl — automated order processing, listing management, and EDI compliance.',
        cta: 'Explore Amazon SP-API',
        href: '/integrationen/amazon-sp-api',
      },
      {
        title: 'Shopware & Shopify',
        description:
          'Order management, returns handling, and ERP synchronization for modern commerce stacks.',
        cta: 'View integrations',
        href: '/integrationen',
      },
      {
        title: 'Custom systems',
        description:
          'Custom ERPs, legacy interfaces, proprietary software — we connect to whatever you run.',
        cta: 'View use cases',
        href: '/use-cases',
      },
    ],
    hoverPrefix: 'MCP server for',
    ctaButton: 'Explore use cases',
    finalCard: {
      title: 'Agent hub orchestration',
      subtitle: 'ERP, shop, marketplace, and CRM operations connected in one safe runtime flow.',
      chips: ['Policy layer', 'Audit trail', 'Failover ready'],
    },
  },
  projects: {
    title: 'Selected projects',
    description: 'Short format: problem -> solution -> result.',
    labels: {
      problem: 'Problem',
      solution: 'Solution',
      result: 'Result',
    },
    cases: [
      {
        name: 'Fuchsversand24 — product data automation',
        problem: 'High manual effort for consistent product descriptions.',
        solution:
          'Built an agent for structured HTML descriptions with context and validation before publishing.',
        result: 'Faster content creation and more consistent storefront presentation.',
      },
      {
        name: 'Playflip — dropshipping automation',
        problem: 'Partner warehouse fulfillment required precise cross-system coordination.',
        solution:
          'Implemented partner warehouse interfaces in Plentymarkets and automated key order workflows.',
        result: 'Fully automated order processing.',
      },
      {
        name: 'Alpenmärchen — peak-season process stability',
        problem: 'Seasonal demand spikes caused operational pressure and process instability.',
        solution: 'Automated critical workflows and hardened them for peak-load phases.',
        result: 'Stable sales operations during high season.',
      },
    ],
  },
  agentsTeaser: {
    label: 'The step after that',
    text: 'Once the integration runs, the next step is an agent that decides on top of it.',
    link: 'Explore AI agents',
  },
  cta: {
    headline: 'Automate today,\nlead tomorrow.',
    headlineHighlight: 'Denqfabrik',
    button: 'Get started',
    card: {
      title: 'Who is behind Denqfabrik?',
      description:
        'A small, specialized team with a direct line — no agency carousel, just dedicated partners who know your system.',
      link: 'Meet the team',
    },
  },
  footer: {
    copyright: `© 2026 ${legalEntity.name}`,
    impressum: 'Imprint',
    datenschutz: 'Privacy',
    linkedin: 'LinkedIn',
    github: 'GitHub',
  },
  structuredData: {
    description:
      'Denqfabrik builds, tests and operates integrations between shop, ERP and marketplace for retail and e-commerce teams — plus AI agents with safety architecture, guardrails, and audit trails.',
    offerDescription:
      'Integrations between shop, ERP and marketplace including build, test, monitoring, and operation.',
  },
  about: {
    hero: {
      label: 'About Denqfabrik',
      headline: 'Commerce automation',
      headlineHighlight: 'with technical depth.',
      subtitle: 'No agency carousel — dedicated partners who know, build, and operate your system.',
    },
    founder: {
      label: 'Founder & technical lead',
      name: 'René Nowotny',
      role: 'Founder & technical lead',
      bio1: 'Three years as CTO of an e-commerce holding company — responsible for technical integration of acquired businesses, from ERP connections to marketplace rollouts.',
      bio2: 'For four years he has been building integrations and automations for commerce teams with Denqfabrik — from single connectors to AI-powered process automation.',
      stats: [
        { value: '7+', label: 'Years in e-commerce' },
        { value: '3', label: 'Years as CTO' },
        { value: '50+', label: 'Integrations built' },
        { value: '20+', label: 'Platforms' },
      ],
    },
    partners: {
      label: 'Core development partners',
      description: 'Specialized engineers working alongside Denqfabrik on commerce automation.',
      list: [
        {
          name: 'Alexander Geils',
          role: 'AI Software Engineer',
          focus:
            'AI-powered agents, LLM integration, data pipelines, production-ready agent architectures.',
          bio: 'Alex brings experience from building AI-powered SaaS products and automating complex business processes. In both startup and enterprise environments, he develops practical digital solutions that streamline operations, simplify processes, and noticeably reduce team workload.',
        },
        {
          name: 'Moritz Hawener',
          role: 'AI Software Engineer',
          focus:
            'MCP servers, API connectors, real-time interfaces, system integration, testing & deployment.',
          bio: 'As a founding engineer and AI specialist, Moritz bridges the gap between complex engineering and measurable business value. With experience building agentic pipelines and advising leadership on AI adoption, he develops solutions that radically simplify processes and make teams more efficient.',
        },
      ],
    },
    principles: {
      label: 'How we work',
      title: 'How we work.',
      list: [
        {
          title: 'Direct line, no agency loop',
          description: 'You always talk to the people who actually build and operate your system.',
        },
        {
          title: 'Operational safety before feature showcase',
          description:
            'Clear scope, clear guardrails, clear accountability — before a single line of code goes to production.',
        },
        {
          title: 'Commerce focus, no generalists',
          description:
            'We work exclusively on processes with direct operational impact in commerce operations.',
        },
      ],
    },
    platform: {
      label: 'Integration experience',
      title: '50+ integrations built.',
      titleFaded: '20+ platforms.',
      description:
        'From ERP to shop systems to marketplaces — we have built and operate production connectors to these systems.',
      platforms: [
        'plentymarkets',
        'Shopify',
        'Shopware',
        'WooCommerce',
        'Amazon SP-API',
        'eBay',
        'OTTO',
        'Kaufland',
        'Mirakl',
        'JTL',
        'Billbee',
        'Xentral',
        'weclapp',
        'Odoo',
        'Afterbuy',
        'ITscope',
        'Lexware',
        'CHECK24',
        'idealo',
        'HubSpot',
        'Pipedrive',
        'Custom ERPs',
      ],
    },
    cta: {
      title: "Let's talk about your process.",
      description:
        'In our first call we clarify which level of automation makes sense and how to get there safely.',
      button: 'Schedule a call',
    },
    meta: {
      title: 'About us',
      description:
        'Denqfabrik builds safe commerce automation with technical depth and clear operational accountability.',
    },
  },
  contact: {
    label: 'Contact',
    titleDefault: "Let's talk",
    titleSelected: 'Almost there!',
    subtitleDefault:
      'Ready to optimize your e-commerce processes? Get in touch for a free initial consultation.',
    subtitleSelected: 'Just a few more details and we will get back to you within 24 hours.',
    callBooking: {
      title: 'Book a 30-minute call',
      subtitle: 'Free & no obligation',
      description: 'Book a slot directly. No sales pitch — honest advice about your project.',
      button: 'Choose a time',
    },
    info: {
      phone: { title: 'Online Meeting', value: 'Book a video call' },
      email: { title: 'Email', value: 'hello@denqfabrik.com' },
      location: { title: 'Location', value: 'Seelze near Hannover, Germany' },
    },
    availability: {
      title: 'Availability',
      days: 'Monday – Sunday',
      hours: '08:00 – 12:00 & 14:00 – 18:00',
    },
    responseTime: 'Response within 24 hours',
    form: {
      nameLabel: 'Name *',
      namePlaceholder: 'Your name',
      emailLabel: 'Email *',
      emailPlaceholder: 'your@email.com',
      companyLabel: 'Company',
      companyPlaceholder: 'Your company',
      phoneLabel: 'Phone',
      phonePlaceholder: '+49 157 890 952 76',
      systemsLabel: 'Which systems do you use? (optional)',
      systemsPlaceholder: 'e.g. Shopify, Plentymarkets, Amazon, ...',
      messageLabel: 'Message *',
      messageLabelSelected: 'Brief description *',
      messagePlaceholder: 'Tell us about your project and requirements...',
      messagePlaceholderSelected: 'What would you like to improve or automate?',
      submitDefault: 'Send message',
      submitSelected: 'Submit request',
      submitting: 'Sending...',
    },
    selectedOption: {
      interestedIn: 'You are interested in:',
      changeSelection: 'Change selection',
      noSelection: 'No selection',
      prefillMessage: 'Interested in: {option}\n\nBrief description of my project:\n',
    },
    status: {
      success: 'Thank you! Your message has been sent successfully.',
      error: 'An error occurred. Please try again later.',
    },
    validation: {
      nameMin: 'Name must be at least 2 characters',
      emailInvalid: 'Please enter a valid email address',
      messageMin: 'Message must be at least 10 characters',
    },
  },
  thankYou: {
    meta: {
      title: 'Thank you for reaching out',
    },
    title: 'Thank you — we received your message.',
    subtitle:
      "We will get back to you within 24 hours, usually much sooner. You'll also receive a confirmation email.",
    nextStep: {
      heading: 'Book a 30-minute call right away',
      description:
        'If it fits your schedule: grab a free slot now. No sales pitch — just an honest take on your project.',
      cta: 'Pick a time',
    },
    backHome: 'Back to home',
  },
  bookingConfirmed: {
    meta: {
      title: 'Booking confirmed — Denqfabrik',
    },
    title: 'Your call is booked.',
    subtitle:
      "We've received your booking. A confirmation with calendar invite and Zoom link is waiting in your inbox.",
    nextStep: {
      heading: 'What happens next',
      description:
        'You\'ll get a reminder shortly before the call. If something comes up: reschedule or cancel right from the confirmation email — no friction.',
    },
    backHome: 'Back to home',
  },
  notFound: {
    title: 'Page not found',
    description: 'The page you requested does not exist. It may have been moved or deleted.',
    button: 'Back to home',
    meta: {
      title: 'Page not found',
    },
  },
  imprint: {
    meta: {
      title: 'Legal Notice',
      description:
        'Legal notice of Denqfabrik: provider identification, register entry and contact details.',
    },
  },
  privacy: {
    meta: {
      title: 'Privacy Policy',
      description:
        'Privacy policy of Denqfabrik: which personal data this website processes, for what purpose, and your rights.',
    },
  },
  blog: {
    meta: {
      title: 'Blog',
      description: 'Insights on e-commerce automation, system integrations and AI agents.',
    },
  },
  kiAgenten: {
    meta: {
      title: 'AI agent use cases',
      description:
        'AI agents automate emails, cancellations, delivery status, and carrier communication in e-commerce.',
    },
    hero: {
      label: 'Use cases',
      titleLine1: 'Your team answers emails.',
      titleLine2: 'So does our agent.',
      subtitle:
        'Faster, more consistent, fully documented — with context from all systems and clear escalation paths.',
    },
    simulation: {
      label: 'Live simulation',
      title: 'An email arrives. The agent takes over.',
      description:
        'Click a scenario to see how the agent pulls context from multiple systems, checks rules, and acts.',
      fromTypeLabels: { kunde: 'Customer', spedition: 'Carrier', intern: 'Internal' },
      agentProcessing: 'Agent processing',
      resultLabels: { systems: 'Systems', duration: 'Duration' },
      fromPrefix: 'From',
    },
    scenarios: [
      {
        id: 'stornierung',
        seoTitle: 'Automatically cancel orders',
        from: 'Anna Miller',
        fromType: 'kunde',
        subject: 'Can I still cancel my order?',
        preview:
          'Hi, I just placed order #38291 but I no longer need the item. Can I still cancel before it ships?',
        time: '09:14',
        steps: [
          {
            label: 'Email classified',
            detail: 'Category: cancellation \u00B7 Confidence: 0.96',
            status: 'success',
          },
          {
            label: 'Order #38291 loaded',
            detail: 'Status: in picking \u00B7 not yet shipped',
            status: 'info',
          },
          {
            label: 'Cancellation possible',
            detail: 'Policy: cancellation before shipment allowed',
            status: 'success',
          },
          {
            label: 'Cancellation executed',
            detail: 'WMS: picking stopped \u00B7 ERP: credit note created',
            status: 'success',
          },
        ],
        result: {
          action: 'Confirmation sent to customer, order cancelled',
          system: 'Shop + WMS + ERP',
          duration: '< 30 seconds',
        },
      },
      {
        id: 'lieferstatus',
        seoTitle: 'Automatically answer delivery status inquiries',
        from: 'Thomas Walker',
        fromType: 'kunde',
        subject: "Where is my delivery? I've been waiting for 5 days.",
        preview:
          'Order number #49382. According to the confirmation it should have arrived by now. Can you tell me where my package is?',
        time: '10:31',
        steps: [
          {
            label: 'Email classified',
            detail: 'Category: delivery status \u00B7 Confidence: 0.93',
            status: 'success',
          },
          {
            label: 'Order #49382 loaded',
            detail: 'Shipping: DHL \u00B7 Tracking: 1234567890',
            status: 'info',
          },
          {
            label: 'Tracking queried',
            detail: 'DHL status: "out for delivery" since this morning',
            status: 'success',
          },
          {
            label: 'Automatic reply',
            detail: 'Tracking link + status update generated',
            status: 'success',
          },
        ],
        result: {
          action: 'Tracking status with link sent to customer',
          system: 'Shop + DHL API',
          duration: '< 15 seconds',
        },
      },
      {
        id: 'spedition',
        seoTitle: 'Automate carrier communication',
        from: 'Schenker Logistics',
        fromType: 'spedition',
        subject: 'Delivery delay: 3 pallets +2 days',
        preview:
          'Dear team, delivery of shipments LKW-2847 will be delayed by approximately 2 business days due to capacity constraints.',
        time: '07:45',
        steps: [
          {
            label: 'Email classified',
            detail: 'Category: delivery delay \u00B7 Sender: carrier',
            status: 'warning',
          },
          {
            label: 'Shipment LKW-2847 resolved',
            detail: '14 orders affected \u00B7 11 customers',
            status: 'info',
          },
          { label: 'SLA check', detail: '3 orders exceed delivery promise', status: 'warning' },
          {
            label: 'Customers proactively notified',
            detail: '11 personalized emails generated and sent',
            status: 'success',
          },
        ],
        result: {
          action: '11 customers proactively notified about delay',
          system: 'WMS + Shop + Email',
          duration: '< 2 minutes',
        },
      },
      {
        id: 'gutschrift',
        seoTitle: 'Automatically verify returns and credit notes',
        from: 'Lisa (Accounting)',
        fromType: 'intern',
        subject: 'Verify credit note for return #8291',
        preview:
          "Hi, can you check whether the credit note for return #8291 is correct? The customer says the amount doesn't match.",
        time: '11:22',
        steps: [
          {
            label: 'Request classified',
            detail: 'Category: credit note verification \u00B7 Internal',
            status: 'info',
          },
          {
            label: 'Return #8291 loaded',
            detail: 'Item returned \u00B7 Condition: pristine \u00B7 Value: \u20AC149.90',
            status: 'info',
          },
          {
            label: 'ERP \u2194 Shop reconciliation',
            detail: 'Credit in ERP: \u20AC129.90 \u00B7 Difference: \u20AC20.00 (shipping costs)',
            status: 'warning',
          },
          {
            label: 'Correction prepared',
            detail: 'Shipping cost refund missing \u00B7 Correction credit note created',
            status: 'success',
          },
        ],
        result: {
          action: 'Difference detected, correction credit note sent to accounting',
          system: 'ERP + Shop',
          duration: '< 45 seconds',
        },
      },
    ],
    impact: {
      label: 'Before / After',
      title: 'What actually changes',
      description:
        'Every request follows the same pattern \u2014 but your team handles it manually today. The agent takes over the legwork; your experts stay in charge of exceptions and escalations.',
      flowLabel: 'Always the same pattern',
      flowSteps: ['Email in', 'Load context', 'Check rules', 'Act or escalate'],
      flowNote:
        'Whether it is a customer, carrier, or internal team \u2014 the agent follows your rules. When uncertain, it escalates to your team.',
      withoutAgent: {
        label: 'Without agent',
        items: [
          {
            text: 'Open multiple systems, gather data manually',
            metric: '10\u201315 min per case',
          },
          {
            text: 'Read, classify, and assign every email individually',
            metric: 'Error-prone at volume',
          },
          { text: 'Customers wait for a response', metric: 'Avg. 2\u20134 hours response time' },
          {
            text: 'Delays only surface when customers complain',
            metric: 'Reactive instead of proactive',
          },
        ],
      },
      withAgent: {
        label: 'With agent',
        items: [
          {
            text: 'Context loaded automatically from all systems',
            metric: 'Shop + ERP + WMS + Logistics',
          },
          { text: 'Emails classified and processed in seconds', metric: '< 30 sec to response' },
          {
            text: 'Customers notified proactively',
            metric: 'e.g. 11 emails for one delivery delay',
          },
          {
            text: 'Escalation on uncertainty \u2014 your experts decide',
            metric: 'Agent handles only the legwork',
          },
        ],
      },
      metrics: [
        { label: 'Processing time', before: '10\u201315 min', after: '< 30 sec' },
        { label: 'Response time', before: '2\u20134 hours', after: 'Instant' },
        { label: 'System lookups', before: '3\u20135 manual', after: 'Automatic' },
        { label: 'Escalations', before: 'All manual', after: 'Exceptions only' },
      ],
    },
    cta: {
      title: 'What emails does your team answer every day?',
      description:
        'Show us 5 typical requests \u2014 we will show you which ones an agent can handle.',
      button: 'Schedule a call',
    },
  },
  useCases: {
    meta: {
      title: 'Use Cases',
      description: 'Concrete deployment scenarios for Denqfabrik agents in commerce operations.',
    },
    hero: {
      label: 'Application scenarios',
      title: 'Use Cases',
      description: 'Concrete deployment scenarios for Denqfabrik agents in commerce operations.',
    },
    caseStudies: {
      label: 'Case Studies',
      title: 'Built. Tested.',
      titleFaded: 'In production.',
      description:
        'Real systems we have developed and shipped to production for commerce companies.',
      detailsButton: 'View details',
      viewAllButton: 'View all use cases',
      cards: [
        {
          slug: 'fulfillment-hub',
          kicker: 'Fulfillment',
          title: 'Central Integration Hub for Fulfillment Clients',
          stat: '3h Onboarding',
          description:
            'Onboard new fulfillment clients in hours instead of weeks. Central hub with a unified API, automated onboarding process, and a real-time dashboard for inventory, orders, and SLA compliance.',
          tags: ['Fulfillment', 'Dashboard', 'Onboarding', 'API Hub'],
        },
        {
          slug: 'repricer',
          kicker: 'Pricing',
          title: 'Multi-Layer Repricer with Four Data Layers',
          stat: '4 Layers',
          description:
            'Intelligent repricing system that simultaneously considers fulfillment capacities, product-level sales data, internal pricing rules, and competitor prices. Fully custom-designed for maximum margin at competitive rates.',
          tags: ['Repricer', 'Custom', 'Warehouse Capacity', 'Competitor Analysis'],
        },
        {
          slug: 'integration-hub',
          kicker: 'Integrations',
          title: 'E-Commerce Integration Hub — All Marketplaces in One Place',
          stat: 'Days not months',
          description:
            'Refunds, Orders, Stocks — all marketplaces in one system. Unit tests, E2E tests, and high security standards. New marketplaces connected in days, even before major connector providers offer an interface.',
          tags: ['Marketplaces', 'E2E Tests', 'Early Access', 'Security'],
        },
        {
          slug: 'dropship-ai',
          kicker: 'Dropship + AI',
          title: 'AI-Powered Dropshipping Integration with Translation',
          stat: 'NL → DE automated',
          description:
            'Integration of a Dutch partner with AI translation of product texts into German. Automatic generation of high-quality HTML descriptions from text and images. Full fulfillment interface for orders, stocks, prices, and refunds.',
          tags: ['AI', 'Translation', 'Fulfillment', 'Dropshipping'],
        },
      ],
    },
    agents: {
      label: 'AI Agents',
      title: 'Agent scenarios.',
      titleFaded: 'Use cases.',
      description:
        'Examples of how our AI agents are deployed in commerce processes — from support to finance.',
      cards: [
        {
          title: 'Support Automation Agent',
          description:
            'Automates standard support requests and scales ticket volume in a controlled way.',
          bullets: [
            'Classification and routing of incoming cases',
            'Standard workflows for cancellations, returns, and status inquiries',
            'Escalation on uncertainty',
          ],
        },
        {
          title: 'Marketplace Operations Agent',
          description:
            'Monitors marketplace processes, detects exceptions, and drives operational responses.',
          bullets: [
            'Monitoring of stock levels and price deviations',
            'Securing vendor and platform processes',
            'Early detection of faulty process chains',
          ],
        },
        {
          title: 'Content & Product Data Agent',
          description:
            'Generates and validates structured product data for shops and marketplaces.',
          bullets: [
            'Generation of structured product descriptions',
            'Consistency checks across attributes and media',
            'Pre-validation before publishing',
          ],
        },
        {
          title: 'Finance & Reconciliation Agent',
          description: 'Automates reconciliation, fee verification, and deviation reporting.',
          bullets: [
            'Reconciliation across ERP and platform data',
            'Flagging fee and settlement differences',
            'Traceable handoff status to finance',
          ],
        },
      ],
    },
    connectors: {
      label: 'Ready-made connectors',
      title: 'Get started faster with existing integrations',
      description:
        'Denqfabrik offers ready-made connectors for common commerce platforms — from marketplaces to shop systems, ERP and fulfillment. No cold start, just production-proven building blocks.',
      button: 'Request a use case workshop',
    },
  },
  useCaseDetail: {
    kicker: 'Use Case',
    includedLabel: 'Included',
    includedTitle: 'What you get out-of-the-box',
    resultsLabel: 'Results',
    resultsTitle: 'What actually changes',
    cta: {
      title: 'Evaluate this use case for your setup',
      description:
        'We jointly assess data readiness, system landscape, and integration effort — and start within weeks.',
      buttonLabel: 'Discuss this use case',
    },
    cases: {
      'fulfillment-hub': {
        title: 'Fulfillment Integration Hub',
        subtitle:
          'Central hub for onboarding new fulfillment clients — with a unified API, automated onboarding, and a real-time dashboard for inventory, orders, and SLA compliance.',
        stats: [
          { value: '3h', label: 'Client onboarding' },
          { value: '50+', label: 'Connected clients' },
          { value: 'Real-time', label: 'Dashboard & monitoring' },
        ],
        sections: [
          {
            title: 'Unified API Layer',
            description:
              'A single interface for all fulfillment clients — regardless of which system the client uses.',
            bullets: [
              'Standardized endpoints for orders, stocks & returns',
              'Automatic data validation and error reporting',
              'Versioned API with clear documentation',
            ],
          },
          {
            title: 'Automated Onboarding',
            description:
              'New clients are connected in hours instead of weeks. Configuration instead of custom development for every individual case.',
            bullets: [
              'Self-service configuration of data mappings',
              'Automatic test runs and validation',
              'Guided setup with progress indicator',
            ],
          },
          {
            title: 'Operations Dashboard',
            description:
              'Real-time overview of all clients, inventory, orders, and SLA compliance — in one place.',
          },
        ],
        included: [
          'Complete API documentation and SDK',
          'Real-time dashboard with client overview',
          'SLA monitoring and alerting',
          'Automated onboarding pipeline',
          'Hosting and operations (included in retainer)',
          'Error handling with retry logic and fallbacks',
        ],
        results: [
          {
            metric: '3 hours instead of 2 weeks for onboarding',
            description:
              'New fulfillment clients are productively connected within a few hours — including test run.',
          },
          {
            metric: 'Real-time transparency for all parties',
            description:
              'Clients and operations always see the current status of inventory, orders, and returns.',
          },
          {
            metric: 'Scalable without additional effort',
            description:
              'Every new client uses the same infrastructure. No additional development effort per connection.',
          },
        ],
      },
      repricer: {
        title: 'Multi-Layer Repricer',
        subtitle:
          'Intelligent repricing system with four data layers — simultaneously considers fulfillment capacities, sales data, internal rules, and competitor prices for maximum margin.',
        stats: [
          { value: '4', label: 'Data layers' },
          { value: 'Custom', label: 'Rule set' },
          { value: 'Real-time', label: 'Price adjustment' },
        ],
        sections: [
          {
            title: 'Four-Layer Architecture',
            description:
              'Instead of one-dimensional price comparison, the repricer works with four independent data layers that flow together in a weighted manner.',
            bullets: [
              'Layer 1: Fulfillment capacities and warehouse inventory',
              'Layer 2: Product-level sales and sell-through data',
              'Layer 3: Internal pricing rules, minimum margins, and locks',
              'Layer 4: Competitor prices and marketplace dynamics',
            ],
          },
          {
            title: 'Rule-Based Price Control',
            description:
              'Your team defines the guardrails — the repricer optimizes automatically within those boundaries.',
            bullets: [
              'Minimum margins and price floors',
              'Category- and season-specific rules',
              'Manual overrides for special cases',
            ],
          },
          {
            title: 'Competitive Analysis',
            description:
              'Competitor prices are continuously captured and included as a data layer in the pricing decision.',
          },
        ],
        included: [
          'Fully custom-designed pricing engine',
          'Dashboard with margin and price overview',
          'Rule editor for your team',
          'Competitor price monitoring',
          'Hosting and operations (included in retainer)',
          'Audit trail for every price change',
        ],
        results: [
          {
            metric: 'Higher margins at competitive rates',
            description:
              'The repricer finds the optimal price between maximum margin and competitiveness — automatically.',
          },
          {
            metric: 'Inventory flows into pricing decisions',
            description: 'Overstock is sold faster, scarce items are automatically priced higher.',
          },
          {
            metric: 'Full control for your team',
            description:
              'Rules, locks, and overrides give your team full authority over pricing at all times.',
          },
        ],
      },
      'integration-hub': {
        title: 'E-Commerce Integration Hub',
        subtitle:
          'All marketplaces in one place — refunds, orders, stocks through a unified interface. With high security standards, unit and E2E tests, and new marketplaces in days instead of months.',
        stats: [
          { value: 'Days', label: 'New marketplaces' },
          { value: '100%', label: 'Test coverage' },
          { value: 'Early Access', label: 'Ahead of the pack' },
        ],
        sections: [
          {
            title: 'Unified Marketplace Interface',
            description:
              'Whether Amazon, eBay, OTTO, or Kaufland — all platforms communicate with your system through one API.',
            bullets: [
              'Normalized data models for orders, stocks & refunds',
              'Platform-specific adapters behind a unified API',
              'Event-based synchronization in real-time',
            ],
          },
          {
            title: 'Quality Assurance & Testing',
            description:
              'Every connector is secured with unit and E2E tests — no surprises in production.',
            bullets: [
              'Unit tests for every piece of business logic',
              'E2E tests against real sandbox environments',
              'Automated regression on API changes',
            ],
          },
          {
            title: 'Fast Marketplace Integration',
            description:
              'New platforms are integrated in days — often before major connector providers even offer an interface.',
          },
        ],
        included: [
          'Modular connector library',
          'Complete test suite (unit + E2E)',
          'Real-time synchronization and event queue',
          'Monitoring and health checks per platform',
          'Hosting and operations (included in retainer)',
          'Documented API with versioning',
        ],
        results: [
          {
            metric: 'Days instead of months for new marketplaces',
            description:
              'New platforms are productively connected within a few days — including tests and monitoring.',
          },
          {
            metric: 'No outages due to tested connectors',
            description: 'Every interface is secured with unit and E2E tests before it goes live.',
          },
          {
            metric: 'Early-access advantage',
            description:
              'You are active on new marketplaces before your competitors even have a connection.',
          },
        ],
      },
      'dropship-ai': {
        title: 'AI Dropshipping Integration',
        subtitle:
          'Integration of a Dutch partner with AI translation of product texts into German, automatic HTML description generation, and a full fulfillment interface.',
        stats: [
          { value: 'NL → DE', label: 'AI translation' },
          { value: 'Auto', label: 'HTML generation' },
          { value: 'Full', label: 'Fulfillment integration' },
        ],
        sections: [
          {
            title: 'AI-Powered Translation',
            description:
              'Product texts are automatically translated from Dutch to German — with context and commerce terminology.',
            bullets: [
              'Contextual translation instead of word-for-word',
              'Commerce-specific vocabulary and tone',
              'Batch processing for large catalogs',
            ],
          },
          {
            title: 'Automatic Content Creation',
            description:
              'High-quality HTML descriptions are generated from translated texts and product images — ready to publish for German marketplaces.',
            bullets: [
              'HTML templates with responsive design',
              'Image integration and media optimization',
              'Format specifications per target channel',
            ],
          },
          {
            title: 'Full Fulfillment Interface',
            description:
              'Orders, stocks, prices, and refunds run automatically between you and the Dutch partner.',
          },
        ],
        included: [
          'AI translation pipeline (NL → DE)',
          'HTML content generator with templates',
          'Fulfillment API for orders, stocks, prices, refunds',
          'Batch import for large product catalogs',
          'Hosting and operations (included in retainer)',
          'Quality checks on translations',
        ],
        results: [
          {
            metric: 'Thousands of products translated automatically',
            description:
              'The entire Dutch catalog is automatically transferred to German — without manual translation.',
          },
          {
            metric: 'Publish-ready HTML descriptions',
            description:
              'High-quality product pages are automatically generated from text and images.',
          },
          {
            metric: 'Seamless process chain with the partner',
            description:
              'Orders, inventory, and returns synchronize automatically — no manual coordination.',
          },
        ],
      },
    },
  },
  solutions: {
    meta: {
      title: 'Platforms & Integrations',
      description:
        'From ERP to shop systems to marketplaces \u2014 stable connectors that run in production.',
    },
    hero: {
      label: 'Platforms & Integrations',
      title: 'We integrate your commerce ecosystem.',
      subtitle:
        'From ERP to shop systems to marketplaces \u2014 stable connectors that run in production.',
    },
    categoriesSection: {
      title: 'Platforms we connect',
      description: 'Four categories, one goal: your systems speak the same language.',
    },
    categories: [
      {
        title: 'ERP Systems',
        tags: ['SAP', 'Odoo', 'weclapp', 'Xentral', 'JTL', 'Lexware', 'Custom ERPs'],
        description:
          'Connecting ERP data and processes as the foundation for automation and agents.',
      },
      {
        title: 'Marketplaces',
        tags: ['Amazon', 'eBay', 'OTTO', 'Kaufland', 'Mirakl', 'CHECK24', 'idealo'],
        description: 'Listing, order management, and EDI compliance for all major marketplaces.',
      },
      {
        title: 'Shop Systems',
        tags: ['Shopify', 'Shopware', 'WooCommerce', 'plentymarkets'],
        description: 'Seamless integration into shop platforms for end-to-end commerce workflows.',
      },
      {
        title: 'CRM & Custom',
        tags: [
          'HubSpot',
          'Salesforce',
          'Pipedrive',
          'Microsoft Dynamics 365',
          'Billbee',
          'Afterbuy',
          'ITscope',
        ],
        description: 'CRM connectivity and proprietary systems with clear contract logic.',
      },
    ],
    processSection: {
      title: 'What an integration includes',
      description:
        'Every integration goes through four phases \u2014 from requirements analysis to production-ready handoff.',
    },
    processSteps: [
      {
        step: '01',
        title: 'Analysis & Scope Definition',
        description:
          'Inventory of your system landscape, prioritization of interfaces, and clear scoping of integration boundaries.',
      },
      {
        step: '02',
        title: 'API Design & Implementation',
        description:
          'Robust interface architecture with error handling, retry logic, and idempotent operations.',
      },
      {
        step: '03',
        title: 'Testing & Monitoring Setup',
        description:
          'End-to-end tests, load tests, and setup of alerting and health checks for production.',
      },
      {
        step: '04',
        title: 'Documentation & Handoff',
        description: 'Technical documentation, runbooks, and structured onboarding for your team.',
      },
    ],
    scriptButler: {
      title: 'Ready to connect your systems?',
      description:
        '30 minutes, free and non-binding. We answer your questions and give an initial assessment.',
      button: 'Schedule initial call',
    },
    cta: {
      title: 'Plan your integration landscape together',
      description: 'We help you prioritize the interfaces that deliver the greatest leverage.',
      button: 'Start an integration conversation',
    },
  },
  solutionDetail: {
    kicker: 'Solutions Detail',
    cta: {
      title: 'Evaluate this integration for your stack',
      description:
        'We assess architecture, dependencies, and security requirements for a stable implementation.',
      buttonLabel: 'Contact',
    },
    cases: {
      'erp-systeme': {
        title: 'ERP Systems',
        subtitle:
          'ERP is the core of operational truth. Agents access order, inventory, and financial context in a structured way.',
        sections: [
          {
            title: 'Data and Process Coupling',
            description: 'Connecting central ERP objects with validated mapping and sync rules.',
          },
          {
            title: 'Transaction Safety',
            description:
              'Idempotent execution, retry strategies, and error handling for stable process chains.',
          },
          {
            title: 'Auditability',
            description: 'Full traceability for agent decisions based on ERP data.',
          },
        ],
      },
      'marktplatz-integrationen': {
        title: 'Marketplace Integrations',
        subtitle:
          'We connect operational marketplace processes with internal systems for reliable automation.',
        sections: [
          {
            title: 'Multi-Channel Operations',
            description:
              'Unified control of inventory, pricing, orders, and exceptions across platform boundaries.',
          },
          {
            title: 'Vendor & Operations Flows',
            description: 'Mapping marketplace-specific logic without breaking internal processes.',
          },
          {
            title: 'Monitoring and Recovery',
            description: 'Early detection of faulty flows and structured recovery strategies.',
          },
        ],
      },
      'commerce-plattformen': {
        title: 'Commerce Platforms',
        subtitle:
          'Shop systems and commerce platforms become stable data sources and action targets for agents.',
        sections: [
          {
            title: 'Product and Catalog Processes',
            description: 'Integration of product data, variant logic, and content flows.',
          },
          {
            title: 'Order and Service Processes',
            description:
              'Connecting orders, shipping, returns, and service cases with a consistent status model.',
          },
          {
            title: 'Scalable Automation',
            description: 'Step-by-step automation with clear boundaries and human-in-the-loop.',
          },
        ],
      },
      'custom-integrationen': {
        title: 'Custom Integrations',
        subtitle:
          'For proprietary systems we build resilient API layers with robust contract and error management logic.',
        sections: [
          {
            title: 'API Design and Contracts',
            description: 'Clear data contracts, versioning, and robust interface architecture.',
          },
          {
            title: 'Security and Access Control',
            description:
              'Permissions, key management, and controlled access to sensitive endpoints.',
          },
          {
            title: 'Operational Readiness',
            description:
              'Monitoring, logging, and maintainability as integral parts of the integration.',
          },
        ],
      },
    },
  },
  amazonSpApi: {
    meta: {
      title: 'Amazon SP-API Integrations',
      description:
        'Professional Amazon SP-API integrations for Vendor and Seller — secure, production-ready, and architecturally sound.',
    },
    hero: {
      label: 'Amazon Developer',
      title: 'Amazon SP-API',
      titleHighlight: 'Integrations.',
      subtitle:
        'We implement robust SP-API processes for Seller and Vendor — from data synchronization and monitoring to operational management of your Amazon channels.',
    },
    capabilities: {
      label: 'Capabilities',
      title: 'What we deliver',
      cards: [
        {
          title: 'SP-API Integration',
          description:
            'Complete Amazon Selling Partner API integration with robust auth flows, rate-limit handling, and structured data synchronization.',
          bullets: [
            'Orders & Fulfillment API',
            'Catalog Items & Listings',
            'Reports & Analytics',
            'Notifications & Event-Bridge',
          ],
        },
        {
          title: 'Vendor Automation',
          description:
            'Automated vendor processes with controlled error handling, clear fallbacks, and escalation logic.',
          bullets: [
            'Purchase Order Processing',
            'Shipment Confirmation',
            'Invoice Automation',
            'Chargebacks & Disputes',
          ],
        },
        {
          title: 'ERP Bridge',
          description: 'Connecting Amazon data with your ERP for consistent operational decisions.',
          bullets: [
            'Inventory Synchronization',
            'Order Import',
            'Invoice Reconciliation',
            'Master Data Mapping',
          ],
        },
        {
          title: 'Credits & Claims',
          description:
            'Transparent preparation and processing of credits including review and escalation logic.',
          bullets: [
            'Shortage Claims',
            'Price Claim Automation',
            'CoOp & Accruals',
            'Dispute Resolution Flows',
          ],
        },
      ],
    },
    security: {
      label: 'Security & Compliance',
      title: 'Amazon-compliant data handling',
      description:
        'We develop according to Amazon Acceptable Use Policy and Data Protection Policy — with documented data retention and clear access concepts.',
      points: [
        {
          title: 'Token & Auth Management',
          description:
            'Secure management of LWA tokens, automatic rotation, and encrypted storage.',
        },
        {
          title: 'PII Handling',
          description:
            'Strict processing of personally identifiable information per Amazon guidelines with retention periods and audit trails.',
        },
        {
          title: 'Rate-Limit Architecture',
          description:
            'Adaptive throttling strategies, burst queues, and retry logic for stable API usage.',
        },
      ],
    },
    pricing: {
      label: 'Pricing',
      title: 'Transparent by scope',
      description:
        'Our pricing is based on integration depth, complexity, and operational requirements. No flat hourly rates — instead, clear scope-based proposals.',
      tiers: [
        {
          name: 'Starter',
          scope: 'Single API integration',
          note: 'e.g. Orders import or Reports sync',
        },
        {
          name: 'Professional',
          scope: 'Multi-API Integration',
          note: 'Vendor + Seller flows, ERP bridge',
        },
        {
          name: 'Enterprise',
          scope: 'Full Automation',
          note: 'End-to-end processes, monitoring, support',
        },
      ],
    },
    cta: {
      title: 'Plan your Amazon SP-API project',
      description:
        'We clarify scope, security requirements, and integration path for your specific use case.',
      button: 'Request Amazon project',
    },
  },
  mirakl: {
    meta: {
      title: 'Mirakl Connector — Conrad, XXXLutz, home24, OBI & more',
      description:
        'Mirakl integration for all operator marketplaces: Conrad, XXXLutz, home24, real, OBI, ManoMano. One connector — many channels. Professionally implemented.',
    },
    hero: {
      label: 'Marketplace Framework',
      title: 'Mirakl',
      titleHighlight: 'Connector.',
      subtitle:
        'Mirakl is the marketplace framework behind Conrad, XXXLutz, home24, real, OBI, and ManoMano. A single connector unlocks all Mirakl-powered channels — without reimplementing per operator.',
    },
    capabilities: {
      label: 'Capabilities',
      title: 'What we deliver',
      cards: [
        {
          title: 'Mirakl API Integration',
          description:
            'Full integration via the Mirakl Operator API — OAuth flow, webhook handling, and structured data synchronization across all connected channels.',
          bullets: ['Orders API', 'Products & Offers Sync', 'Shipping & Tracking', 'Returns & Refunds'],
        },
        {
          title: 'Multi-Channel Configuration',
          description:
            'Each Mirakl operator runs in its own configurable context — no channel bleeding, clear error attribution, channel-specific mapping.',
          bullets: ['Conrad Electronics', 'XXXLutz / Möbelix', 'home24 / real', 'OBI / ManoMano'],
        },
        {
          title: 'Inventory & Price Sync',
          description:
            'Real-time inventory synchronization with configurable buffers, channel-specific pricing rules, and automatic delisting on zero stock.',
          bullets: ['Per-channel stock buffer', 'Channel-specific pricing', 'Availability management', 'Automatic delisting'],
        },
        {
          title: 'Shop System Integration',
          description:
            'Seamless connection with Shopware, Shopify, JTL, plentymarkets, and other commerce systems via standardized interfaces.',
          bullets: ['Shopware & Shopify', 'JTL-Wawi', 'plentymarkets', 'Custom ERPs & legacy systems'],
        },
      ],
    },
    architecture: {
      label: 'Architecture',
      title: 'Scale from one to all channels',
      description:
        'One Mirakl connector — many operator instances. The architecture grows with you: start with one channel, expand to all Mirakl-powered marketplaces without reimplementation.',
      points: [
        {
          title: 'Operator Isolation',
          description:
            'Each Mirakl instance runs in its own configurable context. No channel bleeding, clear error attribution, channel-specific configuration.',
        },
        {
          title: 'Dry-Run Default',
          description:
            'All sync operations start in dry-run mode. Production data is only touched after explicit approval — no accidental overwrites.',
        },
        {
          title: 'Monitoring & Alerting',
          description:
            'Automatic alerts for API errors, missed sync runs, or data anomalies — with job history and detailed error logs.',
        },
      ],
    },
    pricing: {
      label: 'Pricing',
      title: 'Transparent by scope',
      description:
        'Pricing is based on number of channels, integration depth, and operational requirements. Clear scope-based proposals — no flat hourly rates.',
      tiers: [
        {
          name: 'Starter',
          scope: 'Single Mirakl operator',
          note: 'e.g. Conrad or ManoMano',
        },
        {
          name: 'Professional',
          scope: 'Up to 5 operators',
          note: 'Multi-channel with ERP integration',
        },
        {
          name: 'Enterprise',
          scope: 'All operators + automation',
          note: 'Monitoring, operations, SLA',
        },
      ],
    },
    cta: {
      title: 'Request your Mirakl integration',
      description:
        'We clarify channel selection, integration depth, and technical scope — and deliver a concrete proposal.',
      button: 'Request Mirakl project',
    },
  },
  woocommerce: {
    meta: {
      title: 'WooCommerce integrations — marketplaces, shops and ERP',
      description:
        'Connect WooCommerce to marketplaces, shops and ERP — built, tested and operated. Orders, inventory and prices move automatically. No manual re-entry.',
    },
    hero: {
      label: 'WooCommerce Integration',
      title: 'Connect',
      titleHighlight: 'WooCommerce.',
      subtitle:
        'We connect WooCommerce to marketplaces, shops and ERP — built, tested and operated. Orders, inventory and prices move automatically between your systems. No manual re-entry, no toolkit.',
    },
    strip: {
      label: 'Typical counterparts',
      items: [
        'Amazon',
        'Mirakl',
        'Kaufland',
        'OTTO',
        'eBay',
        'JTL',
        'weclapp',
        'Plentymarkets',
        'Afterbuy',
      ],
    },
    capabilities: {
      label: 'Capabilities',
      title: 'What we deliver',
      cards: [
        {
          title: 'Orders & fulfillment',
          description:
            'Orders from marketplaces and shops land in WooCommerce, status and shipment data flow back — including partial shipments and returns.',
          bullets: [
            'Order import from marketplaces',
            'Status and tracking feedback',
            'Partial and follow-up shipments',
            'Returns and refunds',
          ],
        },
        {
          title: 'Inventory & prices',
          description:
            'One stock level, many channels: synchronization with configurable buffers, channel-specific pricing rules, and automatic delisting at zero stock.',
          bullets: [
            'Stock buffer per channel',
            'Channel-specific prices',
            'Automatic delisting',
            'Variant and SKU mapping',
          ],
        },
        {
          title: 'ERP bridge',
          description:
            'WooCommerce talks to your ERP instead of running alongside it. Master data, orders and documents move in both directions.',
          bullets: [
            'JTL-Wawi',
            'weclapp',
            'Plentymarkets & Afterbuy',
            'Accounting export (DATEV, lexware)',
          ],
        },
        {
          title: 'REST API & plugins',
          description:
            'We work on the WooCommerce REST API and write our own plugins where the API falls short — instead of bending your process into someone else’s plugin.',
          bullets: [
            'WooCommerce REST API v3',
            'Webhooks and event processing',
            'Custom endpoints where needed',
            'Migration off stock plugins',
          ],
        },
      ],
    },
    operations: {
      label: 'Operations',
      title: 'Built, tested, operated',
      description:
        'An integration is not finished at go-live — endpoints change, channels change their rules. That is why operating it is part of the delivery.',
      points: [
        {
          title: 'Dry-run by default',
          description:
            'Every sync starts in dry-run mode. Production data is only written after explicit approval — no accidental overwriting of stock levels or prices.',
        },
        {
          title: 'Monitoring & alerting',
          description:
            'Alerts on API errors, missed runs and data anomalies — with job history, so it stays traceable which run wrote which value.',
        },
        {
          title: 'A named contact',
          description:
            'When an integration breaks, daily business burns. You reach the person who built the integration — not a ticket system.',
        },
      ],
    },
    proof: {
      label: 'Evidenced',
      items: [
        'Seven Mirakl marketplaces run in parallel in our day-to-day operations (5× DE, 2× AT) — including inventory and price sync.',
        'For Playflip we built the partner-warehouse interfaces in Plentymarkets: fully automated order processing in dropshipping.',
      ],
    },
    pricing: {
      label: 'Pricing',
      title: 'Transparent by scope',
      description:
        'Price depends on the number of channels, integration depth and operational requirements. Clear scope-based proposals — no blanket hourly rate.',
      tiers: [
        {
          name: 'Starter',
          scope: 'One integration',
          note: 'e.g. WooCommerce ↔ one marketplace',
        },
        {
          name: 'Professional',
          scope: 'Several channels + ERP',
          note: 'Inventory, prices, orders, documents',
        },
        {
          name: 'Enterprise',
          scope: 'Full automation',
          note: 'End-to-end processes, monitoring, operations',
        },
      ],
    },
    cta: {
      title: 'Request your WooCommerce integration',
      description:
        'We clarify channels, data direction and integration depth — and deliver a concrete proposal.',
      button: 'Request WooCommerce project',
    },
  },
  shopware: {
    meta: {
      title: 'Shopware integrations — ERP, marketplaces and accounting',
      description:
        'Connect Shopware to weclapp, JTL, marketplaces and accounting — built, tested and operated. Orders, inventory and prices move automatically. No manual re-entry.',
    },
    hero: {
      label: 'Shopware Integration',
      title: 'Connect',
      titleHighlight: 'Shopware.',
      subtitle:
        'We connect Shopware to your ERP and your sales channels — weclapp, JTL, Plentymarkets, Amazon, Mirakl and more. Orders, inventory and prices move automatically. No manual re-entry, no toolkit.',
    },
    strip: {
      label: 'Common system pairs',
      items: [
        'weclapp ↔ Shopware',
        'JTL ↔ Shopware',
        'Shopware ↔ Amazon',
        'Shopware ↔ Mirakl',
        'Shopware ↔ Kaufland',
        'Shopware ↔ OTTO',
        'A.eins ↔ Shopware',
      ],
    },
    capabilities: {
      label: 'Capabilities',
      title: 'What we deliver',
      cards: [
        {
          title: 'ERP integration',
          description:
            'The most common request and the core of our work: Shopware and ERP as one process instead of two data states someone reconciles by hand.',
          bullets: [
            'weclapp ↔ Shopware',
            'JTL-Wawi ↔ Shopware',
            'A.eins and Plentymarkets',
            'Accounting export (DATEV, lexware)',
          ],
        },
        {
          title: 'Marketplace integration',
          description:
            'Shopware stays the home of your master data; the channels receive offers, stock and prices from it — and hand orders back.',
          bullets: [
            'Amazon (SP-API)',
            'Mirakl marketplaces',
            'Kaufland, OTTO, eBay',
            'Idealo and itscope',
          ],
        },
        {
          title: 'Inventory & prices',
          description:
            'Synchronization with configurable buffers, channel-specific pricing rules and automatic delisting — configurable per channel.',
          bullets: [
            'Stock buffer per channel',
            'Channel-specific prices',
            'Automatic delisting',
            'Variant and SKU mapping',
          ],
        },
        {
          title: 'Shopware 6 API & plugins',
          description:
            'We work on the Shopware 6 Admin and Store API and write our own plugins where the API falls short — no purchased black box in a core process.',
          bullets: [
            'Admin API & Store API',
            'Sync plugins we own',
            'Multi-sales-channel setups',
            'Migration from Shopware 5',
          ],
        },
      ],
    },
    operations: {
      label: 'Operations',
      title: 'Built, tested, operated',
      description:
        'An integration is not finished at go-live — endpoints change, channels change their rules. That is why operating it is part of the delivery.',
      points: [
        {
          title: 'Dry-run by default',
          description:
            'Every sync starts in dry-run mode. Production data is only written after explicit approval — no accidental overwriting of stock levels or prices.',
        },
        {
          title: 'Monitoring & alerting',
          description:
            'Alerts on API errors, missed runs and data anomalies — with job history, so it stays traceable which run wrote which value.',
        },
        {
          title: 'A named contact',
          description:
            'When an integration breaks, daily business burns. You reach the person who built the integration — not a ticket system.',
        },
      ],
    },
    proof: {
      label: 'Evidenced',
      items: [
        'Seven Mirakl marketplaces run in parallel in our day-to-day operations (5× DE, 2× AT) — including inventory and price sync.',
        'For Playflip we built the partner-warehouse interfaces in Plentymarkets: fully automated order processing in dropshipping.',
      ],
    },
    pricing: {
      label: 'Pricing',
      title: 'Transparent by scope',
      description:
        'Price depends on the number of systems, integration depth and operational requirements. Clear scope-based proposals — no blanket hourly rate.',
      tiers: [
        {
          name: 'Starter',
          scope: 'One system pair',
          note: 'e.g. weclapp ↔ Shopware',
        },
        {
          name: 'Professional',
          scope: 'ERP + several channels',
          note: 'Inventory, prices, orders, documents',
        },
        {
          name: 'Enterprise',
          scope: 'Full automation',
          note: 'End-to-end processes, monitoring, operations',
        },
      ],
    },
    cta: {
      title: 'Request your Shopware integration',
      description:
        'Tell us which ERP and which channels are involved — we clarify data direction and scope and deliver a concrete proposal.',
      button: 'Request Shopware project',
    },
  },
  sicherheit: {
    meta: {
      title: 'Safe AI agents by design — Security architecture',
      description:
        'How we build safe AI agents: Controlled pipeline from input to audit trail. Multi-stage verification, guardrails, and EU AI Act compliance.',
    },
    hero: {
      label: 'Security Architecture',
      titleLine1: 'Safe AI agents',
      titleLine2: 'by design.',
      subtitle:
        'Every decision passes through a controlled pipeline — from input to audit trail. No agent goes live without a security architecture.',
    },
    seoHeadings: [
      'How to build safe AI agents?',
      'AI agent security architecture for e-commerce',
      'EU AI Act compliance for AI agents',
    ],
    cards: [
      {
        number: '01',
        title: 'Input & Classification',
        description:
          'Every request is validated, normalized, and classified before entering the pipeline. Cases outside trained patterns are detected and stopped — never guessed.',
        bullets: [
          'Input validation and format checks',
          'Detection of unknown request patterns',
          'Confidence threshold enforces escalation',
        ],
      },
      {
        number: '02',
        title: 'Context & Rule Versions',
        description:
          'The agent loads rules, contracts, and operational data at the time of the decision. When rules change between event and processing, the agent knows which version applies.',
        bullets: [
          'Point-in-time decision context',
          'Versioned business rules',
          'Reproducible decision basis',
        ],
      },
      {
        number: '03',
        title: 'Decision & Verification',
        description:
          'Critical decisions are reviewed by multiple independent models. When they disagree, a human decides — the agent never has the last word on sensitive actions.',
        bullets: [
          'Independent multi-model review',
          'Disagreement triggers human review',
          'Separated responsibilities per stage',
        ],
      },
      {
        number: '04',
        title: 'Guardrails & Permissions',
        description:
          'Every agent operates within clearly defined boundaries. Permissions follow the least-privilege principle, actions are limited, and critical operations are reversible.',
        bullets: [
          'Minimal permissions per agent',
          'Action limits and blocked paths',
          'Rollback-capable operations',
        ],
      },
      {
        number: '05',
        title: 'Audit Trail & Compliance',
        description:
          'Every step is logged — not just what happened, but why. Decision paths document input, context, reasoning, and outcome.',
        bullets: [
          'Complete decision log',
          'Reasoning for every action documented',
          'EU AI Act Art. 13 — transparency requirements',
        ],
      },
    ],
    cta: {
      title: 'How safe is your agent architecture?',
      description:
        'We analyze your existing or planned AI pipeline and identify gaps — from classification to compliance.',
      button: 'Request security check',
    },
  },
  pricingPage: {
    meta: {
      title: 'Pricing — integrations from €109 per month',
      description:
        'Standard integration from €109 per month, each additional script from €30, no setup fee. Ready for Shopify, Amazon, Mirakl, TikTok Shop and A.eins.',
    },
    hero: {
      label: 'Integrations',
      titlePrefix: 'Your integration.',
      titleHighlight: 'From €109 per month.',
      subtitle:
        'Inventory, prices and orders in sync automatically between shop, marketplace and ERP. For Shopify, Amazon, Mirakl, TikTok Shop and A.eins the connection already exists — live in 1–2 weeks once access and a test object are in place. A different system — Afterbuy, JTL, plentymarkets or your ERP? We build it: start date by arrangement — no surcharge.',
      priceLine: 'No setup fee. Minimum term three months from go-live, then cancellable monthly.',
    },
    tiers: [
      {
        title: 'Standard integration',
        strapline:
          'A ready-made connection for a common system. Runs automatically, in the background.',
        price: 'from €109',
        period: '/ month',
        priceNote: 'No setup fee',
        bullets: [
          'Ready for Shopify, Amazon, Mirakl, TikTok Shop and A.eins — live in 1–2 weeks once access and a test object are in place.',
          'A new system? We build it: start date by arrangement — no surcharge.',
          'Inventory, prices or orders stay in sync automatically, several times a day.',
          'Minimum term three months from go-live, then cancellable monthly. No exit fee.',
        ],
        cta: 'Request an integration',
      },
      {
        title: 'Add-on script',
        strapline:
          'Every further automation on your existing connection — booked one by one, added quickly.',
        price: 'from €30',
        period: '/ month per script',
        priceNote: 'No setup fee',
        bullets: [
          'Runs on the connection that is already in place — no new onboarding.',
          'Examples of what customers booked recently: charge shipping only on the first parcel. Create partial refunds automatically. Filter orders by your own rules before they reach the ERP.',
          'You describe the rule, we build and test it against your system.',
          'Special requests welcome — if a rule needs its own logic instead of standard configuration, we tell you the price up front.',
        ],
        cta: 'Request a script',
      },
      {
        title: 'Code ownership',
        strapline: 'Your own Script Butler instance – on your infrastructure.',
        price: 'from €10,000',
        period: 'fixed price',
        priceNote: 'plus maintenance',
        bullets: [
          'Your own dashboard under your own domain.',
          'Your own repository with the Script Butler infrastructure code – runtime, connectors and your flows.',
          'Runs on your own Supabase and Railway accounts: ready to go instead of months of setup.',
          'Your own AI agents can query the server and the code directly.',
          'You keep commissioning new scripts and flows from us. Not part of the package: our agent framework and the skills we use to generate scripts.',
        ],
        footnote: 'For companies that want speed and need to stay independent.',
        cta: 'Request a quote',
      },
    ],
    proofLine:
      'One customer syncs inventory and prices across seven Mirakl marketplaces at once — five in Germany, two in Austria, every two hours, without manual reconciliation.',
    process: {
      title: 'How it works',
      steps: [
        'You send us access to the system we are connecting — API credentials or the required permissions, depending on the system.',
        'You create a test object — usually a test order, or a single test item for inventory or price connections. We check the route against it before anything runs automatically. The reason: a wrong stock level corrects itself on the next run. A duplicated order does not — that is two messages to your buyer.',
        'We run the route end to end once and confirm in writing that it works. Once access and the test object are in place, you are live in one to two weeks on a ready-made system — on a new system, on the agreed start date.',
      ],
    },
    cta: {
      title: 'Ready for your integration?',
      description:
        'Send us a short note on which system you want to connect — we get back to you with an assessment.',
      note: 'No phone call needed. If you would rather talk, just say so in your message.',
      button: 'Request an integration',
    },
    faq: {
      label: 'FAQ',
      title: 'Frequently asked questions',
      items: [
        {
          question: 'What does an integration cost?',
          answer:
            'A standard integration costs from €109 per month — a €79 base plus €30 for the first automation. Each additional script is added from €30 per month. There is no setup fee.',
        },
        {
          question: 'How long am I committed?',
          answer:
            'Minimum term three months from go-live, then cancellable monthly at the end of the month. No exit fee.',
        },
        {
          question: 'How quickly does an integration go live?',
          answer:
            'For Shopify, Amazon, Mirakl, TikTok Shop and A.eins within 1–2 weeks, once access and a test object are in place. For a new system: start date by arrangement — no surcharge.',
        },
        {
          question: 'What if I want to automate something else later?',
          answer:
            'Then you add an add-on script — from €30 per month, on the connection that is already running. No new project, no setup fee.',
        },
        {
          question: 'Can I run it on my own infrastructure?',
          answer:
            'Yes, with code ownership: your own Script Butler instance on your own Supabase and Railway accounts, from €10,000 fixed price plus maintenance.',
        },
      ],
    },
  },
  integrationen: {
    meta: {
      title: 'Integrations',
      description:
        'We connect AI agents securely to your systems — ERP, marketplaces, commerce platforms, and CRM.',
    },
    hero: {
      label: 'Integrations',
      titlePrefix: 'The bridge layer to your',
      titleHighlight: 'business.',
      subtitle:
        'Stable integrations instead of isolated AI islands. We connect agents securely to the systems that run your daily operations.',
    },
    features: [
      {
        title: 'Fault Tolerance',
        items: [
          'Retry & circuit breaker strategies',
          'Graceful degradation',
          'Recovery after outages',
        ],
      },
      {
        title: 'Cross-System',
        items: [
          'Cross-system process chains',
          'Unified data models',
          'Event-based synchronization',
        ],
      },
      {
        title: 'Production-Ready',
        items: ['Monitoring & alerting', 'Health checks', 'No demo integrations'],
      },
      {
        title: 'Custom APIs',
        items: ['Clear contract logic', 'Versioning & docs', 'For proprietary systems'],
      },
    ],
    platforms: {
      label: 'Platforms',
      title: '50+ integrations.',
      titleFaded: '4 categories.',
      description: 'Hover over a category to see all supported platforms.',
    },
    cta: {
      title: 'Plan your integration landscape together',
      description:
        'We help you prioritize the interfaces that deliver the biggest leverage for safe agents.',
      button: 'Start integration conversation',
    },
  },
  platformGrid: {
    categories: {
      erp: 'ERP',
      marketplaces: 'Marketplaces',
      commerce: 'Commerce',
      crm: 'CRM',
    },
  },
  funnel: {
    bookCall: 'Book a call',
    flows: {
      stock: {
        label: 'Inventory sync',
        oneLiner: 'Availability moves automatically in both directions.',
      },
      price: { label: 'Price sync', oneLiner: 'Prices and promotions stay in step.' },
      orders: {
        label: 'Order import',
        oneLiner: 'Orders arrive complete in your leading system.',
      },
      tracking: {
        label: 'Shipping & tracking',
        oneLiner: 'Tracking numbers go back to the channel.',
      },
      returns: {
        label: 'Returns & refunds',
        oneLiner: 'Returns and refunds are written back cleanly.',
      },
      supplier: {
        label: 'Supplier ordering',
        oneLiner: 'Replenishment is triggered at the supplier automatically.',
      },
      receipt: {
        label: 'Goods receipt',
        oneLiner: 'Incoming stock is booked without anyone typing.',
      },
      catalog: {
        label: 'Listings & product data',
        oneLiner: 'New products reach the channel with their attributes.',
      },
      invoices: {
        label: 'Invoices & documents',
        oneLiner: 'Documents are created, corrected and matched.',
      },
      tickets: {
        label: 'Tickets & customer service',
        oneLiner: 'Requests land where your team already works.',
      },
    },
    scaleOptions: {
      daily: 'once a day',
      hourly: 'hourly',
      fast: 'every 5 minutes',
      unknown: "I don't know yet",
    },
    labels: {
      source: {
        title: 'Which system leads at your company?',
        hint: 'Shop or ERP — the system your products and orders live in.',
        customLabel: 'Not listed? Just type it in.',
        customPlaceholder: 'e.g. in-house build, SAP, Xentral …',
      },
      target: {
        title: 'What should it be connected to?',
        hint: 'Multiple choices possible.',
        customLabel: 'Another channel? Just type it in.',
        customPlaceholder: 'e.g. a Mirakl marketplace, a wholesaler, your own API …',
      },
      flows: {
        title: 'What should run automatically?',
        hint: 'Multiple choices. Everything is clickable — including what we do not list as standard.',
        customLabel: 'Edge case? Describe it or paste a spec.',
        customPlaceholder: 'Text from your AI tool is fine — we read it.',
      },
      scale: {
        title: 'How often should it run?',
        hint: 'The cadence drives the effort — not the number of your orders.',
      },
      back: 'Back',
      continueLabel: 'Continue',
      stepIndicator: 'Step {current} of {total}',
      result: {
        title: 'Your project',
        sourceLabel: 'System',
        targetsLabel: 'Channels',
        flowsLabel: 'Flows',
        scaleLabel: 'Cadence',
        notesLabel: 'Notes',
        restart: 'Reset selection',
      },
      close: 'Close',
      nonStandardBadge: 'not standard',
      nonStandardHint:
        'Dashed means: not our standard for this combination. Clickable anyway — our list is not omniscient, and almost anything can be built.',
    },
    outcome: {
      headline: 'How we work.',
      subline:
        'Ready-built integrations live in 1 – 2 weeks, a new system on an agreed start date at no extra cost — monitored in operation after that.',
      build:
        'We build {flows} across {channels} and test against your real data — as a dry run, before anything goes out.',
      flowsOne: 'the flow',
      flowsMany: 'the {count} flows',
      channelsOne: 'one channel',
      channelsMany: '{count} channels',
      manualTest:
        'We test manually, and a human walks your first real order end-to-end through the fulfilment chain.',
      monitored: 'After that it runs monitored: we see errors before you do.',
      directDeveloper: 'You talk to the developer directly — not to a sales team.',
      fastCadence: 'A five-minute cadence we confirm up front — not every interface carries it.',
      nonStandard: 'One edge case in there: {flows} — doable, we will take a quick look together.',
    },
    contact: {
      title: 'Where may we reply?',
      hint: 'We get back to you within 24 hours — usually much sooner.',
      nameLabel: 'Your name',
      emailLabel: 'Email',
      companyLabel: 'Company',
      submitLabel: 'Send request',
      submittingLabel: 'Sending …',
      successTitle: 'Received.',
      successBody:
        'We have your selection and notes, and will get back to you within 24 hours.',
      errorBody: 'That did not go through. Please try again — or book a call directly.',
    },
    briefing: {
      system: 'System',
      channels: 'Channels',
      flows: 'Flows',
      nonStandard: 'Of those, edge cases',
      cadence: 'Cadence',
      notes: 'Notes',
    },
  },
};
